import { Notification } from '../api/notifications';

/**
 * Generate a deep link URL for a notification based on its entity type and ID
 * @param notification - The notification object
 * @returns A URL string for navigation
 */
export const generateNotificationLink = (notification: Notification): string => {
  console.log('🔗 generateNotificationLink called with:', notification);
  
  // If the notification already has a direct link, use it
  if (notification.link) {
    console.log('✅ Using direct link:', notification.link);
    return notification.link;
  }

  const { entity_type, entity_id } = notification;
  
  // Extract project ID from the entity_id or message if available
  const projectId = extractProjectIdFromNotification(notification);
  
  console.log('📍 Extracted project ID:', projectId, 'for entity_type:', entity_type, 'entity_id:', entity_id);
  
  if (!projectId) {
    console.warn('⚠️ No project ID found, redirecting to /projects');
    // Fallback to projects page if no project ID can be determined
    return '/projects';
  }

  // Generate deep links based on entity type
  let link: string;
  switch (entity_type.toLowerCase()) {
    case 'project_item':
    case 'project_plan':
    case 'task':
      // Link to Project Plan tab with the specific item
      link = `/projects/${projectId}?tab=project-plan-2&item=${entity_id}`;
      break;
    
    case 'action_item':
    case 'actionitem':
      // Link to Action Items tab with the specific item
      link = `/projects/${projectId}?tab=action-items&item=${entity_id}`;
      break;
    
    case 'daily_update':
    case 'dailyupdate':
      // Link to Daily Updates tab with the specific item
      link = `/projects/${projectId}?tab=daily-updates&item=${entity_id}`;
      break;
    
    case 'meeting_note':
    case 'meetingnote':
      // Link to Meeting Notes tab with the specific item
      link = `/projects/${projectId}?tab=meeting-notes&item=${entity_id}`;
      break;
    
    case 'project':
      // Link to the project overview
      link = `/projects/${entity_id}`;
      break;
    
    case 'team':
    case 'user':
      // Link to the project team tab
      link = `/projects/${projectId}?tab=team`;
      break;
    
    case 'location':
    case 'room':
      // Link to the project locations tab
      link = `/projects/${projectId}?tab=locations`;
      break;
    
    case 'document':
      // Link to the project documents tab
      link = `/projects/${projectId}?tab=documents`;
      break;
    
    case 'warranty':
      // Link to the project warranties tab
      link = `/projects/${projectId}?tab=warranties`;
      break;
    
    case 'material':
    case 'material_tracker':
      // Link to the project material tracker tab
      link = `/projects/${projectId}?tab=material-tracker`;
      break;
    
    default:
      // For unknown entity types, try to link to the project
      link = `/projects/${projectId}`;
      break;
  }
  
  console.log('🎯 Generated link:', link);
  return link;
};

/**
 * Extract project ID from notification
 * @param notification - The notification object
 * @returns Project ID string or null
 */
const extractProjectIdFromNotification = (notification: Notification): string | null => {
  // First, check if project_id is directly available in the notification
  if (notification.project_id) {
    return notification.project_id;
  }
  
  // Try to extract project ID from the message using common patterns
  const message = notification.message.toLowerCase();
  
  // Look for patterns like "project <id>" or "in project <id>"
  const projectIdMatch = message.match(/(?:project|in project)\s+([a-f0-9-]{36})/i);
  if (projectIdMatch) {
    return projectIdMatch[1];
  }
  
  // Look for patterns in the entity_id if it contains project information
  // This might be a compound ID like "project_id:item_id"
  if (notification.entity_id.includes(':')) {
    const parts = notification.entity_id.split(':');
    if (parts.length >= 2) {
      return parts[0];
    }
  }
  
  // Try to extract from user_id if it's project-related
  // This is a fallback - you might need to adjust based on your actual data structure
  
  return null;
};

/**
 * Get the tab name for a given entity type
 * @param entityType - The entity type from the notification
 * @returns Tab name string
 */
export const getTabForEntityType = (entityType: string): string => {
  switch (entityType.toLowerCase()) {
    case 'project_item':
    case 'project_plan':
    case 'task':
      return 'project-plan-2';
    
    case 'action_item':
    case 'actionitem':
      return 'action-items';
    
    case 'daily_update':
    case 'dailyupdate':
      return 'daily-updates';
    
    case 'meeting_note':
    case 'meetingnote':
      return 'meeting-notes';
    
    case 'team':
    case 'user':
      return 'team';
    
    case 'location':
    case 'room':
      return 'locations';
    
    case 'document':
      return 'documents';
    
    case 'warranty':
      return 'warranties';
    
    case 'material':
    case 'material_tracker':
      return 'assets'; // Redirect to assets since material-tracker is hidden
    
    default:
      return 'project-plan-2'; // Default to project plan
  }
}; 